/**
 * GlobalDropOverlay - Full-screen overlay shown when dragging files over the window
 *
 * This component renders via portal to document.body and shows a semi-transparent
 * overlay with a dashed drop zone when files are being dragged.
 */

import { useEffect } from "react";
import { createPortal } from "react-dom";
import { Plus } from "lucide-react";
import "./GlobalDropOverlay.css";

interface GlobalDropOverlayProps {
  /** Whether the overlay is visible */
  isVisible: boolean;
  /** Called when files are dropped on the overlay */
  onDrop?: (files: File[]) => void;
  /** Called when drag operation ends (drop or leave) */
  onDragEnd?: () => void;
}

export function GlobalDropOverlay({
  isVisible,
  onDrop,
  onDragEnd,
}: GlobalDropOverlayProps) {
  // Use native event listener to ensure we capture the drop
  useEffect(() => {
    if (!isVisible) return;

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        onDrop?.(Array.from(files));
      }
      // Always reset drag state after drop
      onDragEnd?.();
    };

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = "copy";
      }
    };

    // Add listeners with capture phase to ensure we get the event first
    document.addEventListener("drop", handleDrop, true);
    document.addEventListener("dragover", handleDragOver, true);

    return () => {
      document.removeEventListener("drop", handleDrop, true);
      document.removeEventListener("dragover", handleDragOver, true);
    };
  }, [isVisible, onDrop, onDragEnd]);

  if (!isVisible) {
    return null;
  }

  return createPortal(
    <div className="global-drop-overlay">
      <div className="global-drop-overlay__drop-zone">
        <div className="global-drop-overlay__icon">
          <Plus className="w-8 h-8" strokeWidth={1.5} />
        </div>
        <div className="global-drop-overlay__text">
          <h2 className="global-drop-overlay__title">Drop to upload</h2>
          <p className="global-drop-overlay__subtitle">
            Add files to your library
          </p>
        </div>
      </div>
    </div>,
    document.body,
  );
}
